import { Component, Input, OnInit } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { Contact } from "../contact/contact";
import { OptionService } from "../option/option.service";
import { Donation } from "../donation/donation";
import { GlobalValidators } from "../validate/validate.globalvalidators";
import { DonationService } from "../donation/donation.service";
import { UrlService } from "../url/url.service";
import { EmailService } from 'service/email.service';

@Component({
    selector: 'newsletter',
    templateUrl: './newsletter.component.html'
})
export class NewsletterComponent implements OnInit {
    /**
     * Curretn donation
     */
    @Input() donation: Donation;

    /**
     * Formgroup for validating
     */
    @Input() form: FormGroup;

    /**
     * Handling for the contact data
     */
    @Input() contact: Contact;

    /**
     * The current step of the application
     */
    @Input() step: string;

    /**
     * should we ask for newsletter registration
     * @type {any}
     */
    hasNewsletterRegistration: boolean = false;

    /**
     * List of fields to be excluded from the form
     * @type {Array}
     */
    excludedFields: string[] = [];

    /**
     * Indicates if we want to show a error message on the checkboxes
     * @type {boolean}
     */
    showCheckboxError: boolean = false;

    /**
     * holds all mandatory contact fields.
     */
    mandatoryFields: any;

    /**
     * Should the firstname/lastname/salutation field be mandatory when the user wants the newsletter
     */
    hasNewsletterNamerequest: boolean = false;

    /**
     * toggle if the newsletter subscription is finished or not
     */
    newsletterSubscriptionFinished: boolean = false;

    /**
     * List of alle contact fields to avoid conflicts with other formfields from other components if the same form is used.
     */
    public readonly allContactFields = ['firstname', 'lastname', 'email'];

    /**
     * The constructor
     * @param optionService
     * @param donationService
     * @param urlService
     * @param translate
     * @param elementService
     * @param emailService
     */
    constructor(protected optionService: OptionService, protected donationService: DonationService, protected urlService: UrlService, private emailService: EmailService) {
        // get mandatory fields and set validators
        this.mandatoryFields = this.optionService.getOption('mandatory_contact_fields').split(",");
    }

    /**
     * Returns the list of fields that have to be enabled in the current status of the form.
     */
    getFieldsToEnable(): any {
        let enableForms: any = [];
        if (this.hasNewsletterNamerequest === true) {
            // name and salutation should be request when asking for the newsletter
            enableForms = ['firstname', 'lastname'];
        }
        return enableForms;
    }

    /**
     * Updates the validators of the form depending on the current status of the form.
     */
    updateValidators(): void {
        // disable all
        for (let name of this.allContactFields) {
            if (name !== 'email' && typeof this.form.controls[name] !== "undefined") {
                (<FormControl>this.form.controls[name]).disable({ emitEvent: true });
            }
        }

        let enableForms = this.getFieldsToEnable();

        // enable all fields that are needed but only the fields that are not excluded
        for (let i = 0; i < enableForms.length; i++) {
            if (this.excludedFields.indexOf(enableForms[i]) === -1 && typeof this.form.controls[enableForms[i]] !== "undefined") {
                this.form.controls[enableForms[i]].enable({ emitEvent: true });
            }
        }
    }


    /**
     * is called when initialising the component
     * @constructor
     */
    ngOnInit(): void {
        this.hasNewsletterNamerequest = this.optionService.getOption('has_newsletter_namerequest') === true;

        // add email field as mandatory field because we always have this
        this.form.addControl('email', new FormControl('', [Validators.required, GlobalValidators.validateEmail]));

        // add the three checkboxes also to the form, even if they are not validated
        this.form.addControl('wants_newsletter', new FormControl(''));

        this.hasNewsletterRegistration = this.optionService.getOption("has_newsletter_registration");

        // in some cases the newsletter registration was already done before. Hide the element
        if (this.donation.contactData?.wants_newsletter === true) {
          this.hasNewsletterRegistration = false;
        }

        for (let field of this.allContactFields) {
            // set validators for all fields exept email based on the settings for mandatory fields
            if (field != 'email') {
                this.form.addControl(field, new FormControl('', this.isMandatory(field) ? [Validators.required] : null));
            }

            // preset values from the URL
            let fieldValue = this.urlService.findGetParameter("tw_" + field);
            if (fieldValue) {
                this.contact.data[field] = fieldValue;
                this.form.controls[field].setValue(this.contact.data[field]);
            }
        }

        let excludedFields = this.optionService.getOption("exclude_contact_fields");
        if (excludedFields) {
            this.excludedFields = excludedFields.split(",");
        }

        this.updateValidators();

        // preset email
        this.presetEmail();
    }

    /**
     * Returns if a field is invalid
     * @param field
     * @returns {boolean}
     */
    isFieldInvalid(field: string): boolean {
        return this.form.controls[field].touched === true && this.form.controls[field].invalid === true;
    }


    /**
     * Returns the label textkey of a field, depending of it is valid or not
     * @param field
     * @returns {string}
     */
    getLabelKeyForField(field: string): string {
        return this.isFieldInvalid(field) ? 'contact.' + field + '.label.error' : 'contact.' + field + '.label';
    }

    /**
     * tries to preset the meial field from the contact data that was maybe given before.
     */
    presetEmail(): void {
        if (!this.contact.data.email && this.form.controls['email']) {
            this.contact.data.email = this.donation.userEmail;
            this.form.controls['email'].setValue(this.donation.userEmail);
        }
    }

    /**
     * extra submit method for the single newsletter form
     */
    submitNewsletter(): void {
        if (this.form.valid) {
            this.contact.wants_newsletter = true;

            // copy the contact data to a new object
            const newContact = {...this.contact } as any;
            // remove name from the data field if it is not requested
            if (!this.hasNewsletterNamerequest) {
                delete newContact.data.firstname;
                delete newContact.data.lastname;
                delete newContact.data.salutation;
            }
            // update the transaction
            this.donationService.updateDonation(this.contact, this.donation.transactionGuid).subscribe(result => {
                if (result && result.result === 1) {
                    this.newsletterSubscriptionFinished = true;
                }
            });
        }
        else {
            for (let name in this.form.controls) {
                (<FormControl>this.form.controls[name]).markAsTouched({ onlySelf: true });
            }
        }
    }

    /**
     * Returns if a field is mandatory.
     * @param field
     */
    isMandatory(field: string): boolean {
        return this.mandatoryFields.indexOf(field) !== -1;
    }

    /**
    * fix the email address typos
    */
    correctEmailAddress() {
        // get the email address
        const email = this.form.controls['email'].value;

        // fix the email address
        const correctedEmail = this.emailService.correctEmailAddress(email);

        // set the corrected email address
        this.form.controls['email'].setValue(correctedEmail);
        this.contact.data.email = correctedEmail;
    }
}
